import { QueryClient, QueryFunction } from "@tanstack/react-query";
import type { Auth } from "firebase/auth";
import debugLogger from "@/lib/debug-logger";

// Firebase (~100+ KiB) was previously a static import here, which meant every
// page — including public pages that never touch auth — pulled in the full
// SDK as part of the main bundle, since this module is loaded eagerly by
// queryClient (wrapped around the whole app in App.tsx). Load it lazily and
// memoize the promise so every caller in this file awaits the same instance
// instead of re-triggering the import.
let authPromise: Promise<Auth> | null = null;
function loadAuth(): Promise<Auth> {
  if (!authPromise) {
    authPromise = import("@/lib/firebase").then((m) => m.auth);
  }
  return authPromise;
}

async function throwIfResNotOk(res: Response, contextInfo?: { url: string; method: string; contextId?: string }) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    const error = new Error(`${res.status}: ${text}`);
    
    if (contextInfo?.contextId) {
      debugLogger.log('ERROR', 'API request failed', {
        url: contextInfo.url,
        method: contextInfo.method,
        status: res.status,
        statusText: res.statusText,
        responseText: text,
        error: error.message
      }, contextInfo.contextId);
    }
    
    throw error;
  }
}

// In-flight token promise cache: deduplicate concurrent getIdToken() calls within
// a single render cycle. The cache is keyed by UID so it can never cross user boundaries.
// It expires after 5s so a fresh token is fetched on the next request cycle.
interface TokenCacheEntry {
  uid: string;
  promise: Promise<string | null>;
  timestamp: number;
}
let _tokenCache: TokenCacheEntry | null = null;
const TOKEN_CACHE_TTL_MS = 5000;

async function getCachedIdToken(): Promise<string | null> {
  const auth = await loadAuth();
  const user = auth.currentUser;
  if (!user) {
    _tokenCache = null;
    return null;
  }

  const now = Date.now();
  // Invalidate cache on any user change (UID mismatch) or TTL expiry
  if (
    _tokenCache &&
    _tokenCache.uid === user.uid &&
    now - _tokenCache.timestamp < TOKEN_CACHE_TTL_MS
  ) {
    return _tokenCache.promise;
  }

  const tokenStart = Date.now();
  const promise = user.getIdToken().then((token) => {
    console.log(`[perf] Firebase getIdToken() uid=${user.uid} took ${Date.now() - tokenStart}ms`);
    return token;
  }).catch((err) => {
    console.error('[perf] Firebase getIdToken() failed:', err);
    _tokenCache = null;
    return null;
  });

  _tokenCache = { uid: user.uid, promise, timestamp: now };
  return promise;
}

// Helper function to get Firebase ID token for authenticated requests
async function getAuthHeaders(contextId?: string): Promise<Record<string, string>> {
  const auth = await loadAuth();
  const user = auth.currentUser;
  
  if (contextId) {
    debugLogger.log('API', 'Getting auth headers', {
      hasUser: !!user,
      userId: user?.uid,
      userEmail: user?.email
    }, contextId);
  }
  
  if (user) {
    try {
      const token = await getCachedIdToken();
      
      if (token) {
        if (contextId) {
          debugLogger.log('API', 'Firebase token obtained successfully', {
            userId: user.uid,
            tokenLength: token.length,
            hasToken: !!token
          }, contextId);
        }
        
        return {
          'Authorization': `Bearer ${token}`,
        };
      }
    } catch (error) {
      if (contextId) {
        debugLogger.log('ERROR', 'Failed to get Firebase ID token', {
          userId: user?.uid,
          error: error instanceof Error ? error.message : error
        }, contextId);
      } else {
        console.error('Failed to get Firebase ID token:', error);
      }
    }
  }
  
  return {};
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  correlationId?: string
): Promise<Response> {
  // Create or use existing correlation context
  const contextId = correlationId || `api-request-${Date.now()}`;
  if (!correlationId) {
    debugLogger.createContext(contextId, {
      component: 'apiRequest',
      action: 'api-call'
    });
  }

  debugLogger.log('API', 'API request initiated', {
    method,
    url,
    hasData: !!data,
    dataSize: data ? JSON.stringify(data).length : 0,
    correlationId: contextId
  }, contextId);

  const authHeaders = await getAuthHeaders(contextId);
  const headers: Record<string, string> = { ...authHeaders };
  
  // Add correlation ID to headers for backend tracking
  if (contextId) {
    headers['x-correlation-id'] = contextId;
  }
  
  if (data) {
    headers["Content-Type"] = "application/json";
  }

  debugLogger.log('API', 'Request headers prepared', {
    method,
    url,
    hasAuth: !!authHeaders.Authorization,
    correlationId: contextId,
    headers: Object.keys(headers)
  }, contextId);

  // Log request payload (truncated if large)
  if (data) {
    const dataString = JSON.stringify(data);
    debugLogger.log('API', 'Request payload', {
      method,
      url,
      payload: dataString.length > 1000 ? 
        `${dataString.substring(0, 1000)}...[truncated]` : data,
      payloadSize: dataString.length
    }, contextId);
  }

  const requestStart = Date.now();
  
  try {
    const res = await fetch(url, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

    const requestDuration = Date.now() - requestStart;

    debugLogger.log('API', 'API response received', {
      method,
      url,
      status: res.status,
      statusText: res.statusText,
      duration: `${requestDuration}ms`,
      correlationId: contextId,
      responseHeaders: Object.fromEntries(res.headers.entries())
    }, contextId);

    await throwIfResNotOk(res, { url, method, contextId });

    // Log successful response
    debugLogger.log('API', 'API request completed successfully', {
      method,
      url,
      status: res.status,
      duration: `${requestDuration}ms`,
      correlationId: contextId
    }, contextId);

    return res;
  } catch (error) {
    const requestDuration = Date.now() - requestStart;
    
    debugLogger.log('ERROR', 'API request failed', {
      method,
      url,
      error: error instanceof Error ? error.message : error,
      duration: `${requestDuration}ms`,
      correlationId: contextId,
      stack: error instanceof Error ? error.stack : undefined
    }, contextId);
    
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey[0] as string;
    const contextId = `query-${Date.now()}-${url.replace(/[^a-zA-Z0-9]/g, '-')}`;
    
    debugLogger.createContext(contextId, {
      component: 'getQueryFn',
      action: 'query-fetch'
    });

    debugLogger.log('API', 'Query initiated', {
      url,
      queryKey: queryKey.slice(0, 3), // Log first 3 elements to avoid huge arrays
      fullQueryKeyLength: queryKey.length,
      unauthorizedBehavior
    }, contextId);

    const tokenStart = Date.now();
    const authHeaders = await getAuthHeaders(contextId);
    console.log(`[perf] getAuthHeaders for ${url} took ${Date.now() - tokenStart}ms (hasAuth: ${!!authHeaders.Authorization})`);

    const headers = { ...authHeaders, 'x-correlation-id': contextId };

    debugLogger.log('API', 'Query headers prepared', {
      url,
      hasAuth: !!authHeaders.Authorization,
      correlationId: contextId
    }, contextId);

    const requestStart = Date.now();

    try {
      const res = await fetch(url, {
        headers,
        credentials: "include",
      });

      const requestDuration = Date.now() - requestStart;
      console.log(`[perf] fetch ${url} took ${requestDuration}ms (status: ${res.status})`);

      debugLogger.log('API', 'Query response received', {
        url,
        status: res.status,
        statusText: res.statusText,
        duration: `${requestDuration}ms`,
        correlationId: contextId
      }, contextId);

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        debugLogger.log('API', 'Query returned null for 401 (as configured)', {
          url,
          status: res.status,
          unauthorizedBehavior
        }, contextId);
        return null;
      }

      await throwIfResNotOk(res, { url, method: 'GET', contextId });
      
      const responseData = await res.json();

      debugLogger.log('API', 'Query completed successfully', {
        url,
        status: res.status,
        duration: `${requestDuration}ms`,
        responseDataType: Array.isArray(responseData) ? 'array' : typeof responseData,
        responseSize: Array.isArray(responseData) ? responseData.length : 
          typeof responseData === 'object' ? Object.keys(responseData || {}).length : 1,
        correlationId: contextId
      }, contextId);

      return responseData;
    } catch (error) {
      const requestDuration = Date.now() - requestStart;
      
      debugLogger.log('ERROR', 'Query failed', {
        url,
        error: error instanceof Error ? error.message : error,
        duration: `${requestDuration}ms`,
        correlationId: contextId,
        stack: error instanceof Error ? error.stack : undefined
      }, contextId);
      
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
